import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import CTABand from "@/components/CTABand";
import api from "@/lib/api";
import { IMAGES } from "@/lib/content";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [cats, setCats] = useState(["All"]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => {
      setItems(r.data);
      setCats(["All", ...Array.from(new Set(r.data.map((i) => i.category)))]);
    });
  }, []);

  const filtered = cat === "All" ? items : items.filter((i) => i.category === cat);

  return (
    <div className="relative z-10">
      <PageHeader
        overline="Behind the Scenes"
        titleLines={["The", <span key="2" className="text-gold italic">Gallery</span>]}
        subtitle="Consultations, site visits, energy scanning sessions, workshops and events."
        image={IMAGES.energy}
      />
      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-8">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="gallery-filters">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full px-5 py-2.5 text-sm transition-all duration-300 border ${cat === c ? "bg-primary-green text-white border-primary-green" : "border-white/15 text-[#94A3B8] hover:border-gold/40 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>
          <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
            <AnimatePresence>
              {filtered.map((g, i) => (
                <motion.button
                  layout key={g.id}
                  initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.45 }}
                  onClick={() => setLightbox(g)}
                  data-testid={`gallery-item-${i}`}
                  className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 break-inside-avoid"
                >
                  <img src={g.image} alt={g.image_alt || g.title} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060A17]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                    <div>
                      <span className="text-xs text-gold">{g.category}</span>
                      <h3 className="font-display text-xl text-white">{g.title}</h3>
                      {g.image_caption && <p className="text-sm text-white/80 mt-1">{g.image_caption}</p>}
                    </div>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
            data-testid="gallery-lightbox"
          >
            <motion.img initial={{ scale: 0.9 }} animate={{ scale: 1 }} src={lightbox.image} alt={lightbox.title} className="max-h-[85vh] max-w-full rounded-2xl object-contain" />
          </motion.div>
        )}
      </AnimatePresence>
      <CTABand />
    </div>
  );
}
